/**
 * Production adapter — reads the existing Orangecat Digital session cookie
 * via /api/me.php (same account as Home Run Derby). Drop-in: this file is
 * the only place that talks to the Orangecat session.
 *
 * Env (optional, via window or meta): ORANGECAT_AUTH_URL, ORANGECAT_SCORE_API
 */

import type { AuthAdapter, AuthUser, LeaderboardQuery, SubmitResult } from "./types";
import type { Grade, LeaderboardRow, Profile, SubmitPayload } from "../types";

function apiRoot(): string {
  const w = window as unknown as { ORANGECAT_SCORE_API?: string; ORANGECAT_AUTH_URL?: string };
  if (w.ORANGECAT_SCORE_API) return w.ORANGECAT_SCORE_API.replace(/\/$/, "");
  const meta = document.querySelector('meta[name="orangecat-score-api"]');
  if (meta) return (meta.getAttribute("content") || "").replace(/\/$/, "");
  // From /fact-trail/ the PHP API lives at ../api
  const here = location.pathname;
  if (here.includes("/fact-trail")) return here.replace(/\/fact-trail.*/, "/api");
  return "api";
}

async function api(path: string, opts?: { method?: string; body?: unknown }): Promise<any> {
  const url = `${apiRoot()}/${path.replace(/^\//, "")}`;
  const init: RequestInit = {
    method: opts?.method || "GET",
    credentials: "include",
    headers: {},
  };
  if (opts?.body != null) {
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(url, init);
  try {
    return await res.json();
  } catch {
    return { success: false, error: "Invalid server response.", _status: res.status };
  }
}

function toUser(u: any): AuthUser {
  return {
    id: String(u.id),
    nickname: u.username || u.nickname || "Player",
    email: u.email || null,
    isParent: true,
    source: "orangecat",
  };
}

export const orangecatAdapter: AuthAdapter = {
  id: "orangecat",

  async getSession() {
    try {
      const data = await api("me.php");
      if (data && data.auth && data.user) return toUser(data.user);
    } catch {
      /* offline */
    }
    return null;
  },

  async login(username, password) {
    try {
      const data = await api("login.php", { method: "POST", body: { login: username, password } });
      if (data.success && data.user) return { ok: true, user: toUser(data.user) };
      return { ok: false, error: data.error || "Could not sign in." };
    } catch {
      return { ok: false, error: "Network error." };
    }
  },

  async register(username, password, email) {
    try {
      const body: Record<string, string> = { username, password };
      if (email) body.email = email;
      const data = await api("register.php", { method: "POST", body });
      if (data.success && data.user) return { ok: true, user: toUser(data.user) };
      return { ok: false, error: data.error || "Could not create account." };
    } catch {
      return { ok: false, error: "Network error." };
    }
  },

  async logout() {
    try {
      await api("logout.php", { method: "POST" });
    } catch {
      /* */
    }
  },

  async submitScore(payload: SubmitPayload): Promise<SubmitResult> {
    try {
      const data = await api("ft_score.php", { method: "POST", body: payload });
      if (data && data.success) {
        return { ok: true, score: data.score, rank: data.rank };
      }
      if (data && data._status === 401) return { ok: false, needsAuth: true, error: "Sign in to save." };
      return { ok: false, rejected: !!data?.rejected, error: data?.error || "Could not save score." };
    } catch {
      return { ok: false, error: "Network error." };
    }
  },

  async getLeaderboard(q: LeaderboardQuery): Promise<LeaderboardRow[]> {
    try {
      const p = new URLSearchParams({
        board: q.board,
        grade: String(q.grade),
      });
      if (q.classCode) p.set("class", q.classCode);
      if (q.dateISO) p.set("date", q.dateISO);
      const data = await api("ft_leaderboard.php?" + p.toString());
      return data.leaderboard || [];
    } catch {
      return [];
    }
  },

  async getProgress(grade: Grade) {
    try {
      const data = await api("ft_progress.php?grade=" + grade);
      if (data && data.success) return { stars: data.stars || [], unlocked: data.unlocked || 1 };
    } catch {
      /* */
    }
    return null;
  },

  async saveProgress(grade: Grade, stars: number[]) {
    try {
      await api("ft_progress.php", { method: "POST", body: { grade, stars } });
    } catch {
      /* */
    }
  },

  async listProfiles(): Promise<Profile[]> {
    try {
      const data = await api("ft_profiles.php");
      return data.profiles || [];
    } catch {
      return [];
    }
  },

  async saveProfile(p: Profile) {
    try {
      await api("ft_profiles.php", { method: "POST", body: p });
    } catch {
      /* */
    }
  },

  async generateClassCode() {
    try {
      const data = await api("ft_class.php", { method: "POST", body: { action: "generate" } });
      return data.code || "";
    } catch {
      return "";
    }
  },
};
